import { inject, Injectable } from '@angular/core';
import { TreeNode } from 'primeng/api';
import { BehaviorSubject, map, Observable } from 'rxjs';

import { HisBackendService } from '../../core/service/backend/his-backend.service';
import { HisUidService } from '../../core/service/base/his-uid.service';
import { isNil, requireNonNil } from '../../core/utils';
import { HisTreeNode, HisTreeNodeData, RawTreeNode } from '../model/his-tree.model';

@Injectable({providedIn: 'root'})
export class HisTreeService {
    private hisBackendService = inject(HisBackendService);

    /**
     * Loads a tree from the given REST URL.
     * @param treeName
     * @param treeDataUrl
     * @deprecated Use `loadHisTree` instead, which returns a strongly typed `HisTreeNode` structure.
     */
    public loadTree( treeName: string, treeDataUrl: string ): BehaviorSubject<TreeNode[]> {
        console.log( 'HisTreeService: treeName =', treeName, ', treeDataUrl =', treeDataUrl );

        const subject = new BehaviorSubject<TreeNode[]>( null );
        if ( !treeDataUrl ) {
            console.error( 'HisTreeService: No REST service URL registered for tree', treeName );
            return subject;
        }

        this.loadHisTree( treeDataUrl ).subscribe( {
            next: rootNode => subject.next( [rootNode] ),
            error: err => console.warn( '#loadTree: tree is null or failed to load', err )
        } );
        return subject;
    }

    /**
     * Loads a tree from the given REST URL and parses it into a `HisTreeNode` structure.\
     * The returned observable fails if the fetched tree is nil.
     * @param treeDataUrl the URL of the REST service providing the tree data.
     * @param T the type of the tree node data. (aka. row data model)
     * @returns an observable emitting the parsed `HisTreeNode` root node. (must be subscribed to trigger request)
     */
    public loadHisTree<T extends HisTreeNodeData = HisTreeNodeData>( treeDataUrl: string ): Observable<HisTreeNode<T>> {
        return this.hisBackendService.getData( treeDataUrl ).pipe( map( ( tree: RawTreeNode<T> ) => {
            requireNonNil( tree, '#loadHisTree: tree is null!' );
            return this.parseHisTreeNode( tree, undefined, { isFirst: true, isLast: true, index: 0 } );
        } ) );
    }

    /**
     * Container method for all post-processings of trees after they have been loaded.
     * Should be called before a tree is used in tree components.
     * @param root the root node of the tree.
     * @deprecated legacy method mutating the original tree. Use `parseHisTreeNode` instead for better type safety.
     */
    public postProcessTree( root: TreeNode ): void {
        for ( const node of this.iterateTreeNodes( [root] ) ) {
            const checkboxUid: string = HisUidService.get( 'checkbox.' );
            node.data ??= {};
            node.data.checkbox1Id = checkboxUid + '.1';
            node.data.checkbox2Id = checkboxUid + '.2';
            Object.assign( node, { uid: HisUidService.get( 'node.' ) } );

            node.children ??= [];
            if ( node.children.length > 0 && node.children.some( isNil ) ) {
                console.warn( '#postProcessTree: node', node, 'contains null children!' );
                node.children = node.children.filter( child => child !== null );
            }
        }
    }

    /**
     * Iterates over all tree nodes in a tree (depth-first).
     * @param root the root nodes of the tree from which to start iteration. Does not have to be the actual root.
     */
    public *iterateTreeNodes<T extends TreeNode>( root: T[] ): Generator<T> {
        for ( const node of root ) {
            yield node;
            if ( node.children && node.children.length > 0 ) {
                yield* this.iterateTreeNodes( node.children as T[] );
            }
        }
    }

    /**
     * Updates the positional flags (`isFirst`, `isLast`, `index`) of all tree nodes in a tree recursively (depth-first).
     * Can be called after a tree has been modified (e.g. after a cut/paste operation) to ensure that the flags are correct.
     * 
     * Assumes each flag is modifiable at runtime, regardless of interface definition, to keep any direct object references intact.
     * @param nodes the array of tree nodes to update.
     */
    public updateTreeNodePositionFlags( nodes: any[], recursive = false ): void {
        nodes.forEach( ( node, index, arr ) => {
            Object.assign( node, { isFirst: index === 0, isLast: index === arr.length - 1, index } );
            if ( recursive && node.children && node.children.length > 0 ) {
                this.updateTreeNodePositionFlags( node.children, true );
            }
        } );
    }

    /**
     * Parses a raw tree node into a `HisTreeNode` structure recursively. Already initializes the positional flags (`isFirst`, `isLast`, `index`) of the nodes.
     * @param node the raw tree node to parse.
     * @param parent the parent `HisTreeNode`, if any.
     * @param options additional options for parsing.
     * @returns the parsed `HisTreeNode`.
     */
    public parseHisTreeNode<T extends HisTreeNodeData = HisTreeNodeData>( node: RawTreeNode<T>, parent?: HisTreeNode<T>, options: { isFirst?: boolean, isLast?: boolean, index?: number } = {} ): HisTreeNode<T> {
        // generate unique checkbox html id's, required for "Barrierefreiheit"
        const checkboxUid: string = HisUidService.get( 'checkbox.' );
        const data = {
            ...node.data,
            checkbox1Id: checkboxUid + '.1',
            checkbox2Id: checkboxUid + '.2'
        };
        const { isFirst = false, isLast = false, index = -1 } = options;
        const treeNode: HisTreeNode<T> = {
            ...node,
            uid: HisUidService.get( 'node.' ),
            data,
            children: [],
            parent,
            isFirst,
            isLast,
            index
        };
        if ( node.children && node.children.length > 0 ) {
            treeNode.children = node.children.filter( this.filterNullNodes( parent ) )
                .map( ( child, idx, arr ) =>
                    this.parseHisTreeNode( child, treeNode, { isFirst: idx === 0, isLast: idx === arr.length - 1, index: idx } )
                );
        }
        return treeNode;
    }

    private filterNullNodes( parentNode: HisTreeNode ) {
        return function( node: any ): boolean {
            if ( !node ) {
                console.warn( '#parseHisTreeNode: node', parentNode, 'contains a null child!!' );
                return false;
            }
            return true;
        };
    }
}
